import { useState, useEffect, useCallback } from 'react';

export interface Capabilities {
  [key: string]: boolean;
}

interface CapabilitiesState {
  capabilities: Capabilities;
  orgId: number | null;
  orgName: string | null;
  /** Staff/superuser: org role matrix is bypassed on the API; mirror that in the SPA. */
  allCapabilities: boolean;
  loading: boolean;
  error: string | null;
}

export function useCapabilities(
  orgId?: number | null,
  /** Mirrors backend: staff/superuser bypass org role matrix (survives caps API errors). */
  grantAllCapabilities?: boolean
) {
  const grantAll = Boolean(grantAllCapabilities);
  const [state, setState] = useState<CapabilitiesState>(() => ({
    capabilities: {},
    orgId: null,
    orgName: null,
    allCapabilities: grantAll,
    loading: !grantAll,
    error: null,
  }));

  const fetchCapabilities = useCallback(async () => {
    if (!grantAll) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    } else {
      setState(prev => ({ ...prev, error: null }));
    }
    try {
      const url = orgId
        ? `/api/user/capabilities/?org_id=${orgId}`
        : '/api/user/capabilities/';
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setState({
          capabilities: data.capabilities || {},
          orgId: data.org_id ?? null,
          orgName: data.org_name ?? null,
          allCapabilities: grantAll || Boolean(data.all_capabilities),
          loading: false,
          error: null,
        });
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load capabilities',
          allCapabilities: grantAll || prev.allCapabilities,
        }));
      }
    } catch {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load capabilities',
        allCapabilities: grantAll || prev.allCapabilities,
      }));
    }
  }, [orgId, grantAll]);

  useEffect(() => {
    if (grantAll) {
      setState(prev => ({
        ...prev,
        allCapabilities: true,
        loading: false,
      }));
    }
  }, [grantAll]);

  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  const can = useCallback(
    (capabilityKey: string): boolean => {
      if (grantAll) return true;
      if (state.allCapabilities) return true;
      return state.capabilities[capabilityKey] === true;
    },
    [grantAll, state.allCapabilities, state.capabilities]
  );

  return {
    can,
    capabilities: state.capabilities,
    orgId: state.orgId,
    orgName: state.orgName,
    allCapabilities: state.allCapabilities,
    loading: state.loading,
    error: state.error,
    refresh: fetchCapabilities,
  };
}
